import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import { brand } from '../theme';

/**
 * 8-bit loaders: a building that rises floor by floor (ConstructionLoader) and
 * three pixel dots for inline / button-sized waits (PixelDots).
 *
 * Both are drawn on a pixel grid with crispEdges and animated with steps() so
 * they move frame by frame. Colors come from the brand tokens in theme.ts.
 */

export type LoaderSurface = 'light' | 'dark';

/** Pixel colors per surface: periwinkle on navy, darker blue on white. */
const palette = {
  dark: { line: brand.accent, hi: brand.textDarkBg, dim: 'rgba(154, 186, 246, 0.35)' },
  light: { line: brand.accentOnLight, hi: brand.textLightBg, dim: 'rgba(47, 95, 196, 0.3)' },
} as const;

// ---------------------------------------------------------------------------
// ConstructionLoader
// ---------------------------------------------------------------------------

const CYCLE = '3.2s';

// Each floor pops in at its own point in the cycle; everything clears at 88%.
const floorIn = (at: number) => keyframes`
  0% { opacity: 0; }
  ${at}% { opacity: 1; }
  88% { opacity: 0; }
  100% { opacity: 0; }
`;
const FLOOR_KEYFRAMES = [8, 22, 36, 50, 64, 76].map(floorIn);

const blink = keyframes`
  0%, 80% { opacity: 1; }
  82% { opacity: 0.3; }
  84%, 100% { opacity: 1; }
`;
const beacon = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.2; }
`;

type Px = { c: 'line' | 'hi' | 'dim'; x: number; y: number; w: number; h: number; win?: boolean };

/** One storey: two walls, a slab and two windows, `top` rows down from the grid top. */
const storey = (top: number, left: Px['c'], right: Px['c'], winLeft = false, winRight = false): Px[] => [
  { c: 'line', x: 6, y: top, w: 1, h: 3 },
  { c: 'line', x: 17, y: top, w: 1, h: 3 },
  { c: 'line', x: 6, y: top, w: 12, h: 1 },
  { c: left, x: 9, y: top + 1, w: 2, h: 1, win: winLeft },
  { c: right, x: 13, y: top + 1, w: 2, h: 1, win: winRight },
];

/** Floors bottom to top; the last entry is the roof mast with its beacon. */
const FLOORS: Px[][] = [
  storey(18, 'hi', 'hi', true),
  storey(15, 'hi', 'dim'),
  storey(12, 'dim', 'hi', false, true),
  storey(9, 'hi', 'hi'),
  storey(6, 'hi', 'dim', true),
  [{ c: 'line', x: 11, y: 3, w: 1, h: 3 }, { c: 'hi', x: 11, y: 2, w: 1, h: 1, win: true }],
];

export interface ConstructionLoaderProps {
  /** Rendered width and height in px. Default 64. */
  size?: number;
  /** Accessible text announced to screen readers. Default "Loading". */
  label?: string;
  /** 0 to 100. When given, shows that share of the floors instead of looping. */
  progress?: number;
  /** Surface the loader sits on. Default "light" (white content panels). */
  surface?: LoaderSurface;
}

export const ConstructionLoader: React.FC<ConstructionLoaderProps> = ({
  size = 64,
  label = 'Loading',
  progress,
  surface = 'light',
}) => {
  const colors = palette[surface];
  const determinate = typeof progress === 'number' && Number.isFinite(progress);
  const pct = determinate ? Math.min(100, Math.max(0, progress as number)) : 0;
  const builtFloors = Math.round((pct / 100) * FLOORS.length);
  const isRoof = (i: number) => i === FLOORS.length - 1;

  const animated = !determinate;

  return (
    <Box
      component="svg"
      role="status"
      aria-label={determinate ? `${label}, ${Math.round(pct)}%` : label}
      viewBox="0 0 24 24"
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        display: 'block',
        flexShrink: 0,
        shapeRendering: 'crispEdges',
        '& .line': { fill: colors.line },
        '& .hi': { fill: colors.hi },
        '& .dim': { fill: colors.dim },
        ...(animated && {
          '& .floor': { opacity: 0 },
          ...Object.fromEntries(
            FLOOR_KEYFRAMES.map((kf, i) => [
              `& .floor-${i}`,
              { animation: `${kf} ${CYCLE} steps(1, end) infinite` },
            ])
          ),
          '& .win': { animation: `${blink} ${CYCLE} steps(1, end) infinite` },
          '& .beacon': { animation: `${beacon} 1.2s steps(1, end) infinite` },
        }),
        '@media (prefers-reduced-motion: reduce)': {
          '& *': { animation: 'none !important' },
          // Looping mode shows the finished building when motion is reduced.
          ...(animated && { '& .floor': { opacity: 1 } }),
        },
      }}
    >
      {/* Ground line */}
      <rect className="line" x={2} y={21} width={20} height={1} />
      {FLOORS.map((pixels, i) => (
        <g
          key={i}
          className={`floor floor-${i}`}
          data-built={determinate ? String(i < builtFloors) : undefined}
          style={determinate ? { opacity: i < builtFloors ? 1 : 0 } : undefined}
        >
          {pixels.map((p, j) => (
            <rect
              key={j}
              className={[p.c, p.win ? (isRoof(i) ? 'beacon' : 'win') : ''].join(' ').trim()}
              x={p.x}
              y={p.y}
              width={p.w}
              height={p.h}
            />
          ))}
        </g>
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// PixelDots
// ---------------------------------------------------------------------------

const dotOn = keyframes`
  0% { opacity: 1; }
  33.333% { opacity: 0.25; }
`;

export interface PixelDotsProps {
  /** Height of the row in px (each dot is size/3 wide, with gaps). Default 12. */
  size?: number;
  /** Accessible text announced to screen readers. Default "Loading". */
  label?: string;
  /**
   * "inherit" (default) uses the current text color, so it matches whatever
   * button or line it sits in. "light" / "dark" use the surface tokens.
   */
  surface?: LoaderSurface | 'inherit';
}

/** Three square dots lighting up in sequence, for loading states under ~24px. */
export const PixelDots: React.FC<PixelDotsProps> = ({ size = 12, label = 'Loading', surface = 'inherit' }) => {
  const color = surface === 'inherit' ? 'currentColor' : palette[surface].line;
  return (
    <Box
      component="svg"
      role="status"
      aria-label={label}
      // 3 dots of 3 units with 2-unit gaps: 13 x 3 grid.
      viewBox="0 0 13 3"
      sx={{
        width: `${Math.round((size * 13) / 3)}px`,
        height: `${size}px`,
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
        shapeRendering: 'crispEdges',
        '& rect': {
          fill: color,
          opacity: 0.25,
          animation: `${dotOn} 0.9s steps(1, end) infinite`,
        },
        '& rect:nth-of-type(2)': { animationDelay: '0.3s' },
        '& rect:nth-of-type(3)': { animationDelay: '0.6s' },
        '@media (prefers-reduced-motion: reduce)': {
          '& rect': { animation: 'none', opacity: 1 },
        },
      }}
    >
      <rect x={0} y={0} width={3} height={3} />
      <rect x={5} y={0} width={3} height={3} />
      <rect x={10} y={0} width={3} height={3} />
    </Box>
  );
};
